import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { toast } from "react-toastify";
import ConfirmDialog from "../ConfirmDialog";
import LoadingOverlay from "../LoadingOverlay";
import { TbFileText } from "react-icons/tb";

const lbl =
  "block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1";
const inp =
  "w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-400";

function DocumentModal({ dataSet, onClose, refreshData }) {
  const [file, setFile] = useState(null);
  const [fileLabel, setFileLabel] = useState("No file chosen");
  const [fileName, setFileName] = useState("");
  const [issueDate, setIssueDate] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [uploadPending, setUploadPending] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (dataSet) {
      const cleaned = dataSet.filename
        .split(".")
        .slice(0, -1)
        .join(".")
        .replace(/_@\d+$/, "");
      setFileName(cleaned.toUpperCase());
      setIssueDate(new Date(dataSet.issue_date));
    }
  }, [dataSet]);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f) {
      setFile(f);
      setFileLabel(f.name);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please select a file.");
      return;
    }
    if (!fileName) {
      toast.error("Please enter a name.");
      return;
    }
    setUploadPending(true);
    setShowConfirm(true);
  };

  const handleUpload = async () => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      const ext = file.name.split(".").pop();
      formData.append("file", file);
      formData.append("customFileName", `${fileName}_@${Date.now()}.${ext}`);
      formData.append("fileId", dataSet.id);
      formData.append("issue_date", format(issueDate, "yyyy-MM-dd"));

      const urlMap = {
        icp: "icp/patch",
        msds: "msds/patch",
        spec: "spec/patch",
      };
      const endpoint = urlMap[dataSet.type];
      if (!endpoint) {
        toast.error("Unknown document type.");
        return;
      }

      await axios.patch(`https://eps.elkomprintama.com/${endpoint}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Document updated.");
      refreshData();
      onClose();
    } catch {
      toast.error("Upload failed.");
    } finally {
      setIsLoading(false);
      setUploadPending(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative">
      {isLoading && <LoadingOverlay message="Uploading..." />}

      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div>
          <h2 className="text-sm font-bold text-gray-800">Edit Document</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Replace file or update issue date
          </p>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition"
        >
          &times;
        </button>
      </div>

      <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
        {/* Material name */}
        <div>
          <label className={lbl}>Material Name</label>
          <input
            className={inp}
            name="filename" type="text"
            placeholder="Material name"
            value={fileName}
            onChange={(e) => setFileName(e.target.value.toUpperCase())}
          />
        </div>

        {/* File picker */}
        <div>
          <label className={lbl}>Replacement File</label>
          <div
            className="w-full border-2 border-dashed border-gray-300 rounded-lg px-3 py-4 text-center cursor-pointer hover:border-teal-400 transition"
            onClick={() => fileRef.current?.click()}
          >
            <TbFileText className="mx-auto mb-1 text-gray-300" size={24} />
            <p className="text-xs text-gray-400 truncate px-2">{fileLabel}</p>
            <p className="text-xs text-teal-500 font-semibold mt-0.5">
              Click to browse
            </p>
          </div>
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>

        {/* Issue date */}
        <div>
          <label className={lbl}>Issue Date</label>
          <DatePicker
            selected={issueDate}
            onChange={setIssueDate}
            dateFormat="dd/MM/yyyy"
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-400"
            wrapperClassName="w-full"
          />
          <p className="text-xs text-gray-400 mt-1">
            {dataSet?.type === "msds" &&
              "⏱ MSDS expires 5 years from issue date"}
            {dataSet?.type === "icp" && "⏱ ICP expires 1 year from issue date"}
            {dataSet?.type === "spec" && "✓ Spec documents do not expire"}
          </p>
        </div>

        <div className="flex gap-2 pt-1">
          <button
            type="submit"
            className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-teal-600 hover:bg-teal-700 transition"
          >
            Update
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 transition"
          >
            Cancel
          </button>
        </div>
      </form>

      {showConfirm && uploadPending && (
        <ConfirmDialog
          message="Replace the existing file? This cannot be undone."
          onConfirm={() => {
            setShowConfirm(false);
            handleUpload();
          }}
          onCancel={() => {
            setShowConfirm(false);
            setUploadPending(false);
          }}
        />
      )}
    </div>
  );
}

export default DocumentModal;
